import { useEffect, useState } from "react";
import {
  bindingConflict,
  chordFromEvent,
  formatChord,
  reservedReason,
  shortcutLabel,
  shortcutsFor,
  type ShortcutCommand,
  type ShortcutHost,
} from "@cuekards/core/shortcuts";
import { useShortcutBindings } from "./use-shortcuts.js";

/** What the note at the top says, and the one sentence on this screen whose
 * wording differs between the hosts — one of them is a window and the other is a
 * tab, and "focused" means a different thing to each. */
const NOTE: Record<ShortcutHost, string> = {
  desktop:
    "These work while this window is focused, which is where you are while the camera is rolling. Choose a key Windows has no use of its own for — anything else is refused, with what Windows does with it.",
  web: "These work while this tab is focused, which is where you are while the camera is rolling. Choose a key the browser has no use of its own for — anything else is refused, with what the browser does with it.",
};

/** What heads the screen, where the chrome around it does not head it already.
 * The desktop's Shortcuts tab holds two screens — these keys and `VoicePanel`
 * beneath them — so an unheaded table would start the tab unlabelled and then
 * name its second half; the heading matches the one `VoicePanel` brings, down to
 * the rule that styles it. The web's settings page is a list of sections it
 * labels itself, and does label this one (`SettingsScreen.tsx`), so a heading
 * here would be the same words twice. */
const HEADING: Record<ShortcutHost, string | null> = {
  desktop: "Keyboard shortcuts",
  web: null,
};

export interface ShortcutPanelProps {
  host: ShortcutHost;
}

/**
 * Which key runs which command, and how to change one (FR-52, story 38).
 *
 * Literally the same screen on both hosts, which is what this package is for.
 * The host picks four things and nothing else: which rows there are, which keys
 * are refused, whether the note says "window" or "tab", and whether the screen
 * heads itself or is headed by the page around it. Everything else — one capture
 * at a time, Esc backs out, a refusal says what has the key — is the same problem
 * wherever it is being solved.
 *
 * The desktop had no screen like this at all: its keys were fixed and all it
 * offered was a switch per command, which is the deviation from FR-52 that
 * `docs/manual-test.md` §K recorded. In-window shortcuts make rebinding cheap
 * enough that the requirement is met rather than reduced.
 */
export function ShortcutPanel({ host }: ShortcutPanelProps): JSX.Element {
  const specs = shortcutsFor(host);
  const bindings = useShortcutBindings(host);
  /** The command whose next keypress is being waited for, or null. One at a
   * time: two rows capturing at once would both claim the same key. */
  const [capturing, setCapturing] = useState<ShortcutCommand | null>(null);
  const [problem, setProblem] = useState<string>();

  useEffect(() => {
    if (capturing === null) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      const chord = chordFromEvent(event);
      // A modifier on its own is somebody on their way to a chord, not a chord.
      if (chord === null) return;
      // In the capture phase and stopped here, so that nothing else on the page
      // — a focused button, a browser feature the page could swallow — sees the
      // key being *chosen* as a key being *pressed*.
      event.preventDefault();
      event.stopPropagation();

      // Checked before the reserved list, which also has an opinion about Esc:
      // backing out is what Esc is for everywhere else, and this is a modal
      // moment.
      if (chord === "Escape") {
        setCapturing(null);
        setProblem(undefined);
        return;
      }

      const reserved = reservedReason(host, chord);
      if (reserved !== null) {
        // What the desktop's "a binding failed to register" used to be. The
        // problem is knowable in advance, so it is said before the key is taken
        // rather than after it stops working.
        setProblem(`${formatChord(chord)} — ${reserved}.`);
        return;
      }
      const clash = bindingConflict(specs, bindings, capturing, chord);
      if (clash !== null) {
        setProblem(
          `${formatChord(chord)} is already ${shortcutLabel(clash).toLowerCase()}.`,
        );
        return;
      }

      setCapturing(null);
      setProblem(undefined);
      window.cuekards.setShortcutBinding(capturing, chord);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [capturing, bindings, host, specs]);

  return (
    <section className="settings-screen">
      {HEADING[host] !== null && <h2>{HEADING[host]}</h2>}

      <p className="settings-note">{NOTE[host]}</p>

      <ul className="hotkey-list">
        {specs.map((spec) => {
          const listening = capturing === spec.command;
          return (
            <li className="hotkey-row" key={spec.command}>
              <span className="hotkey-label">{spec.label}</span>
              <button
                type="button"
                className={`hotkey-key${listening ? " listening" : ""}`}
                data-command={spec.command}
                onClick={() => {
                  setProblem(undefined);
                  setCapturing(listening ? null : spec.command);
                }}
                title={
                  listening
                    ? "Press the key you want, or Esc to leave it as it is"
                    : "Change this shortcut"
                }
              >
                {listening ? "Press a key…" : formatChord(bindings[spec.command])}
              </button>
            </li>
          );
        })}
      </ul>

      {problem !== undefined && (
        <p className="web-error" role="alert">
          {problem}
        </p>
      )}

      <div className="settings-actions">
        <button
          type="button"
          className="settings-reset"
          onClick={() => {
            setCapturing(null);
            setProblem(undefined);
            window.cuekards.resetShortcuts();
          }}
          title="Put every shortcut back on its built-in key"
        >
          Reset to defaults
        </button>
      </div>
    </section>
  );
}
